import { useState, useMemo } from 'react'
import { useProductivitySessions } from './hooks/useProductivitySessions'
import { resolveMediaUrl } from '../../../utils/mediaUrl'
import AppDateInput from '../../../components/AppDateInput'
import AppDropdown from '../../../components/AppDropdown'
import AppPagination from '../../../components/AppPagination'
import { AppTooltip } from '../../../components/AppTooltip'
import StatusMeaningModal from './components/StatusMeaningModal'
import PersistentStatusLegendBar from './components/PersistentStatusLegendBar'
import {
  SessionStatusBadge,
  LogoutLogicBadge,
  TimelineLogoutDisplay,
} from './components/SessionGlossaryBadge'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'LIVE', label: 'LIVE' },
  { value: 'VALIDATED', label: 'VALIDATED' },
  { value: 'RECOVERED', label: 'RECOVERED' },
]

function initials(name) {
  return String(name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}



export default function ProductivitySessionsListScreen() {
  const { state, actions } = useProductivitySessions()
  const [showLegendModal, setShowLegendModal] = useState(false)

  const weeklyBars = state.weeklyBars

  const maxBarHours = Math.max(...weeklyBars.map((row) => Number(row.active || 0) + Number(row.idle || 0)), 1)

  const systemHealth = useMemo(() => {
    if (!state.rows.length) return { pct: 99.8, validatedCount: 0, total: 0 }
    const validatedCount = state.rows.filter((row) => row.status === 'VALIDATED').length
    const pct = Number(((validatedCount / state.rows.length) * 100).toFixed(1))
    return { pct, validatedCount, total: state.rows.length }
  }, [state.rows])

  const userDropdownOptions = useMemo(
    () => [
      { value: 'all', label: 'All Users' },
      ...state.userOptions.map((option) => ({ value: String(option.id), label: option.name })),
    ],
    [state.userOptions],
  )

  const departmentDropdownOptions = useMemo(
    () => [
      { value: 'all', label: 'All Departments' },
      ...state.departmentOptions.map((option) => ({ value: String(option.id), label: option.name })),
    ],
    [state.departmentOptions],
  )

  return (
    <div className="mx-auto w-full max-w-[1320px] pb-10">
      <header className="mb-8">
        <div className="mb-6 flex flex-col justify-between gap-6 xl:flex-row xl:items-end">
          <div>
            <h1 className="text-[2.35rem] font-extrabold tracking-tight text-on-surface">Productivity Sessions</h1>
            <p className="mt-2 max-w-4xl text-base leading-relaxed text-on-surface-variant">
              Analyze operational efficiency and session integrity. Real-time data of user login patterns, duration anomalies, and productivity metrics.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={actions.exportReport}
              disabled={state.isExporting}
              className="inline-flex items-center gap-2 rounded-lg bg-secondary-container px-6 py-3 text-base font-bold text-on-secondary-container disabled:cursor-not-allowed disabled:opacity-60"
            >
              {state.isExporting ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                  Exporting...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-lg">download</span>
                  Export Report
                </>
              )}
            </button>
          </div>
        </div>

        <section className="rounded-[2rem] bg-surface-container-low p-6">
          <div className="mb-5 inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.15em] text-on-surface-variant">
            <span className="material-symbols-outlined text-sm">filter_alt</span>
            Advanced Filters
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <label className="grid gap-2">
              <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">User</span>
              <AppDropdown
                value={state.userId}
                onChange={(value) => actions.setUserId(String(value))}
                options={userDropdownOptions}
                triggerClassName="rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface shadow-none"
              />
            </label>

            <label className="grid gap-2">
              <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Department</span>
              <AppDropdown
                value={state.departmentId}
                onChange={(value) => actions.setDepartmentId(String(value))}
                options={departmentDropdownOptions}
                triggerClassName="rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface shadow-none"
              />
            </label>

            <label className="grid gap-2">
              <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Date From</span>
              <AppDateInput
                value={state.dateFrom}
                onChange={actions.setDateFrom}
                inputClassName="rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface shadow-none"
              />
            </label>

            <label className="grid gap-2">
              <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Date To</span>
              <AppDateInput
                value={state.dateTo}
                onChange={actions.setDateTo}
                inputClassName="rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface shadow-none"
              />
            </label>

            <label className="grid gap-2">
              <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Status</span>
              <AppDropdown
                value={state.status}
                onChange={(value) => actions.setStatus(String(value))}
                options={STATUS_OPTIONS}
                triggerClassName="rounded-xl border-0 bg-surface-container-lowest px-4 py-3 text-sm text-on-surface shadow-none"
              />
            </label>
          </div>
        </section>
      </header>

      {state.error ? (
        <div className="mb-6 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}

      <PersistentStatusLegendBar onOpenFullModal={() => setShowLegendModal(true)} />

      <section className="overflow-hidden rounded-[2rem] bg-surface-container-lowest">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1180px] border-collapse text-left">
            <thead>
              <tr className="border-b border-outline-variant/15 bg-surface-container-low">
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">User</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Timeline (In/Out)</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Duration</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Efficiency Track</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Logout Logic</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-container">
              {state.isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    Loading productivity sessions...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    No sessions found for selected filters.
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr key={row.sessionId} className="hover:bg-surface-container-low">
                    <td className="px-6 py-5 align-top">
                      <div className="flex items-center gap-3">
                        {resolveMediaUrl(row.profilePhotoPath) ? (
                          <img
                            src={resolveMediaUrl(row.profilePhotoPath)}
                            alt={row.userName}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-fixed text-sm font-black text-primary">
                            {initials(row.userName)}
                          </span>
                        )}
                        <div>
                          <p className="text-base font-bold text-on-surface">{row.userName}</p>
                          <p className="text-xs text-on-surface-variant">{row.roleName || row.departmentName}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <div className="space-y-1.5">
                        <p className="text-xs font-bold uppercase tracking-[0.12em] text-on-surface-variant">{row.sessionDate}</p>
                        <p className="inline-flex items-center gap-2 text-sm font-medium text-on-surface">
                          <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          {row.loginTime}
                        </p>
                        <div className="pt-0.5">
                          <TimelineLogoutDisplay
                            logoutTime={row.logoutTime}
                            isLiveSession={Boolean(row.isLiveSession || row.status === 'LIVE')}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <p className="text-lg font-bold text-on-surface">{row.durationLabel}</p>
                      <p className="mt-1 text-sm text-on-surface-variant">Expected: {row.expectedLabel}</p>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <div>
                        <div className="mb-1 flex items-center justify-between text-sm font-semibold text-on-surface">
                          <span>{row.productiveLabel} Productive</span>
                          <span className="text-primary">{row.efficiencyPercent}%</span>
                        </div>
                        <div className="mb-2 text-xs text-on-surface-variant">Idle: {row.idleLabel}</div>
                        <div className="h-2.5 w-48 rounded-full bg-surface-variant">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${Math.max(3, Math.min(100, Number(row.efficiencyPercent || 0)))}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <LogoutLogicBadge
                        reason={row.logoutReason || row.logoutType}
                        isLiveSession={Boolean(row.isLiveSession || row.status === 'LIVE')}
                      />
                    </td>
                    <td className="px-6 py-5 align-top">
                      <SessionStatusBadge status={row.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-outline-variant/15 bg-surface-container-low px-6 py-4">
          <p className="text-sm font-medium text-on-surface-variant">
            Showing {state.rangeStart} to {state.rangeEnd} of {state.totalCount} sessions
          </p>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-5 xl:grid-cols-12">
        <article className="rounded-[2rem] bg-surface-container-lowest p-7 xl:col-span-8">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-4xl font-extrabold tracking-tight text-on-surface">Productive Output Matrix</h3>
              <p className="mt-1 text-base text-on-surface-variant">Cross-departmental engagement over last 24h.</p>
            </div>
            <div className="inline-flex items-center gap-4 text-sm font-bold text-on-surface-variant">
              <span className="inline-flex items-center gap-1">
                <span className="h-3 w-3 rounded-full bg-primary" />
                ACTIVE
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-3 w-3 rounded-full bg-surface-container-highest" />
                IDLE
              </span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-7 items-end gap-3 rounded-2xl bg-surface-container-low p-4">
            {weeklyBars.length ? weeklyBars.map((bar) => {
              const total = Number(bar.active || 0) + Number(bar.idle || 0)
              const activeHeight = Math.max(8, Math.round((Number(bar.active || 0) / maxBarHours) * 220))
              const idleHeight = Math.max(8, Math.round((Number(bar.idle || 0) / maxBarHours) * 220))
              return (
                <div key={bar.day} className="flex flex-col items-center gap-2">
                  <div className="flex h-[230px] w-full items-end justify-center gap-2">
                    <AppTooltip content={`Active ${bar.active}h`}>
                      <div className="w-12 rounded-t-md bg-primary" style={{ height: `${activeHeight}px` }} />
                    </AppTooltip>
                    <AppTooltip content={`Idle ${bar.idle}h`}>
                      <div className="w-12 rounded-t-md bg-surface-container-highest" style={{ height: `${idleHeight}px` }} />
                    </AppTooltip>
                  </div>
                  <p className="text-xs font-black uppercase tracking-[0.13em] text-on-surface-variant">{bar.day}</p>
                  <p className="text-[11px] font-bold text-on-surface-variant">{total.toFixed(1)}h</p>
                </div>
              )
            }) : (
              <div className="col-span-7 py-10 text-center text-sm text-on-surface-variant">No cumulative session data for selected filters.</div>
            )}
          </div>
        </article>

        <aside className="flex flex-col gap-5 xl:col-span-4">
          <article className="rounded-[2rem] bg-primary p-7 text-on-primary">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary-fixed-dim">System Health</p>
            <h3 className="mt-2 text-4xl font-black">{systemHealth.pct}%</h3>
            <p className="mt-3 text-base leading-relaxed text-primary-fixed">
              Session synchronization is currently optimal across all nodes.
            </p>
            <div className="mt-5 h-2 w-full rounded-full bg-white/25">
              <div className="h-full rounded-full bg-white" style={{ width: `${Math.max(3, systemHealth.pct)}%` }} />
            </div>
            <p className="mt-3 text-sm text-primary-fixed-dim">
              {systemHealth.validatedCount} validated of {systemHealth.total} observed sessions
            </p>
          </article>

          <article className="rounded-[2rem] bg-surface-container-lowest p-7">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary">Next Audit</p>
            <h3 className="mt-2 text-4xl font-extrabold tracking-tight text-on-surface">Oct 24, 2023</h3>
            <p className="mt-3 text-base leading-relaxed text-on-surface-variant">
              Compliance check scheduled for core infrastructure.
            </p>
          </article>
        </aside>
      </section>

      <StatusMeaningModal
        isOpen={showLegendModal}
        onClose={() => setShowLegendModal(false)}
      />
    </div>
  )
}
